<template>
  <v-container fluid>
    <v-row class="mb-5">
      <v-col cols="12">
        <v-card outlined class="rounded-lg pa-4">
          <v-card-title class="text-h6 font-weight-bold pb-1"> Planungsübersicht für Zeitraum: {{ formattedDateRange }} </v-card-title>
          <v-card-subtitle>Basierend auf der Wochen-Vorlage (Schritt 1)</v-card-subtitle>
          <v-row class="mt-2" align="center">
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text">Benötigte Arbeitsstunden</div>
              <div v-if="loadingTarget" class="text-center mt-2"><v-progress-circular indeterminate size="24" width="2"></v-progress-circular></div>
              <div v-else class="text-h5 font-weight-bold primary--text">{{ targetHours.toFixed(1) }} h</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text">Entspricht ca. Vollzeitäquivalent</div>
              <div v-if="loadingTarget" class="text-center mt-2"><v-progress-circular indeterminate size="24" width="2"></v-progress-circular></div>
              <div v-else class="text-h5 font-weight-bold primary--text">{{ targetPercentage.toFixed(0) }} %</div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-caption grey--text mb-1">Abdeckung durch 'Bereit'-markierte Mitarbeiter</div>
              <v-progress-linear :value="coveragePercentage" :color="coverageColor" height="25" rounded class="mb-1 progress-bar-animation">
                <template v-slot:default="{ value }">
                  <strong style="color: white; text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5)">{{ Math.ceil(value) }}%</strong>
                </template>
              </v-progress-linear>
              <div class="text-caption grey--text text-right">
                {{ selectedPercentageSum.toFixed(0) }}% von {{ targetPercentage.toFixed(0) }}% benötigt
              </div>
            </v-col>
          </v-row>
          <v-row class="mt-3">
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Prognose Gesamtbilanz (Mitarbeiter-Soll vs. Bedarf)</div>
              <div v-if="loadingTarget || loading" class="mt-2"><v-progress-circular indeterminate size="20" width="2"></v-progress-circular></div>
              <div v-else class="text-h6 d-flex align-center" :class="prognosisTotalColor">
                {{ totalSollHoursSelected.toFixed(1) }}h (Soll) vs. {{ targetHours.toFixed(1) }}h (Bedarf) =
                <strong class="ml-1">{{ prognosisTotalHours.toFixed(1) }} h</strong>
                <v-icon small :color="prognosisTotalColor.replace('--text', '')" class="ml-1">{{ prognosisTotalIcon }}</v-icon>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Prognose Ø pro Mitarbeiter</div>
              <div v-if="loadingTarget || loading || readyEmployeesCount === 0" class="mt-2">
                <v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
              </div>
              <div v-else class="text-h6 d-flex align-center" :class="prognosisAvgColor">
                <strong>{{ prognosisAverageHours.toFixed(1) }} h</strong>
                <v-icon small :color="prognosisAvgColor.replace('--text', '')" class="ml-1">{{ prognosisAvgIcon }}</v-icon>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <v-card-title class="text-h5 font-weight-bold mb-4 d-flex align-center pt-0"> Mitarbeiter für Planung auswählen & konfigurieren </v-card-title>

    <v-row v-if="loading">
      <v-col class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 grey--text">Lade Mitarbeiterdaten...</p>
      </v-col>
    </v-row>

    <v-row v-else dense>
      <v-col v-for="employee in employees" :key="employee._id" cols="12" md="6" lg="4">
        <v-card
          class="d-flex flex-column fill-height rounded-lg elevation-2 employee-card"
          :class="{ 'ready-card': employee.planningSettings.isReadyForPlanning }">
          <v-card-title class="d-flex align-center">
            <v-avatar size="48" class="mr-4">
              <v-img :src="'/img/profilbilder' + employee.picture" :alt="employee.fullName">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
                <template v-slot:error>
                  <v-avatar color="primary" size="48">
                    <span class="white--text text-h6">{{ employee.fullName ? employee.fullName.slice(0, 1) : "?" }}</span>
                  </v-avatar>
                </template>
              </v-img>
            </v-avatar>
            <div>
              <div class="text-h6">{{ employee.fullName }}</div>
              <div v-if="getStudioInfo(employee)" class="text-subtitle-1 grey--text">{{ getStudioInfo(employee).title }}</div>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="flex-grow-1">
            <div class="font-weight-bold mb-2">Stellenpensen</div>
            <div v-for="studioInfo in employee.studio" :key="studioInfo._id" class="mb-2">
              <v-row align="center" no-gutters :class="{ 'grey--text text--lighten-1': studioInfo._id !== studioId }">
                <v-col cols="6" class="font-weight-medium text-body-2">
                  {{ getStudioName(studioInfo._id) }}
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="getPlanningPercentage(employee, studioInfo)"
                    @change="updatePercentage(employee, studioInfo._id, $event)"
                    :disabled="studioInfo._id !== studioId"
                    type="number"
                    suffix="%"
                    dense
                    hide-details
                    solo
                    flat
                    class="text-right percentage-input"
                    :class="{ 'primary--text font-weight-bold': studioInfo._id === studioId }"></v-text-field>
                </v-col>
              </v-row>
            </div>

            <div v-if="employee.vacations && employee.vacations.length > 0">
              <v-divider class="my-3"></v-divider>
              <div class="font-weight-bold mb-2">Geplante Ferien im Zeitraum</div>
              <v-chip small color="blue" outlined v-for="vacation in employee.vacations" :key="vacation._id" class="ma-1">
                <v-icon left small>mdi-palm-tree</v-icon>
                {{ vacation.formattedRange }}
              </v-chip>
            </div>

            <v-divider class="my-3"></v-divider>

            <v-switch
              v-model="employee.isApprentice"
              label="Lehrling"
              color="primary"
              dense
              hide-details
              @change="savePermanentSettings(employee)"></v-switch>

            <transition name="slide-fade">
              <div v-if="employee.isApprentice" class="mt-2 pl-2 apprentice-settings">
                <div class="font-weight-bold mb-2">Schultage</div>
                <v-chip-group multiple v-model="employee.schoolDaysOfWeek" active-class="primary--text" @change="updateSchoolDays(employee)">
                  <v-chip v-for="day in weekDays.slice(0, 5)" :key="day.value" :value="day.value" small>
                    {{ day.text }}
                  </v-chip>
                </v-chip-group>
                <v-switch
                  v-model="employee.cannotOpenOrCloseShift"
                  label="Darf Center nicht öffnen/schliessen"
                  color="primary"
                  dense
                  hide-details
                  class="mt-2"
                  @change="savePermanentSettings(employee)"></v-switch>
              </div>
            </transition>

            <v-divider class="my-3"></v-divider>

            <div class="font-weight-bold mb-2">Temporäre Einstellungen (nur für diesen Plan)</div>
            <div class="mt-4">
              <div class="mb-2">Wiederkehrende fixe Abwesenheit</div>
              <v-chip-group
                multiple
                v-model="employee.planningSettings.recurringAbsence"
                active-class="orange--text text--darken-2"
                @change="updatePlanningSettings(employee)">
                <v-chip v-for="day in weekDays" :key="day.value" :value="day.value" small>
                  {{ day.text }}
                </v-chip>
              </v-chip-group>
            </div>

            <v-divider class="my-3"></v-divider>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-flex justify-center align-center mt-3">
                  <span
                    class="mr-2 text-subtitle-1 font-weight-medium"
                    :class="employee.planningSettings.isReadyForPlanning ? 'success--text' : 'grey--text'">
                    {{ employee.planningSettings.isReadyForPlanning ? "Bereit" : "Nicht bereit" }}
                  </span>
                  <v-switch
                    v-model="employee.planningSettings.isReadyForPlanning"
                    color="success"
                    dense
                    hide-details
                    inset
                    class="mt-0 pt-0 ready-switch"
                    @change="updatePlanningSettings(employee)"></v-switch>
                </div>
              </template>
              <span>Mitarbeiter für automatische Zuweisung berücksichtigen</span>
            </v-tooltip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"
import Vue from "vue"

export default {
  name: "ShiftManagementEmployeeSettings",
  props: {
    schoolHolidays: { type: Array, default: () => [] }, // NEUES PROP
    studioId: { type: String, required: true },
    planningStartDate: { type: String, required: true },
    planningEndDate: { type: String, required: true },
    weeklyTemplateData: { type: Array, default: () => [] },
    availableShifts: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: true,
      loadingTarget: true,
      employees: [],
      targetHours: 0,
      targetPercentage: 0,
      weekDays: [
        { text: "Mo", value: 1 },
        { text: "Di", value: 2 },
        { text: "Mi", value: 3 },
        { text: "Do", value: 4 },
        { text: "Fr", value: 5 },
        { text: "Sa", value: 6 },
        { text: "So", value: 7 }, // ISO Weekday
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    formattedDateRange() {
      const start = this.planningStartDate ? this.$moment(this.planningStartDate).format("DD.MM.YYYY") : ""
      const end = this.planningEndDate ? this.$moment(this.planningEndDate).format("DD.MM.YYYY") : ""
      return `${start} - ${end}`
    },
    readyEmployees() {
      return Array.isArray(this.employees) ? this.employees.filter((emp) => emp.planningSettings?.isReadyForPlanning) : []
    },
    readyEmployeesCount() {
      return this.readyEmployees.length
    },
    selectedPercentageSum() {
      return this.readyEmployees.reduce((sum, emp) => {
        const studioInfo = emp.studio.find((s) => s._id === this.studioId)
        return sum + (studioInfo ? this.getPlanningPercentage(emp, studioInfo) : 0)
      }, 0)
    },
    coveragePercentage() {
      if (!this.targetPercentage || this.targetPercentage === 0) return 0
      const percentage = (this.selectedPercentageSum / this.targetPercentage) * 100
      return Math.min(percentage, 100)
    },
    coverageColor() {
      const percentage = this.coveragePercentage
      if (percentage < 85) return "warning"
      if (percentage < 100) return "info"
      return "success"
    },
    totalSollHoursSelected() {
      if (!Array.isArray(this.readyEmployees)) return 0
      return this.readyEmployees.reduce((sum, emp) => {
        return sum + this.calculateSollStunden(emp, emp.planningSettings)
      }, 0)
    },
    prognosisTotalHours() {
      return this.totalSollHoursSelected - (this.targetHours || 0)
    },
    prognosisTotalColor() {
      const diff = this.prognosisTotalHours
      if (diff < -8) return "error--text"
      if (diff > 8) return "warning--text"
      return "success--text"
    },
    prognosisTotalIcon() {
      const diff = this.prognosisTotalHours
      if (diff < -8) return "mdi-arrow-down-bold-circle-outline"
      if (diff > 8) return "mdi-arrow-up-bold-circle-outline"
      return "mdi-check-circle-outline"
    },
    prognosisAverageHours() {
      if (!this.readyEmployeesCount || this.readyEmployeesCount === 0) return 0
      return this.prognosisTotalHours / this.readyEmployeesCount
    },
    prognosisAvgColor() {
      const avgDiff = this.prognosisAverageHours
      if (avgDiff < -2) return "error--text"
      if (avgDiff > 2) return "warning--text"
      return "success--text"
    },
    prognosisAvgIcon() {
      const avgDiff = this.prognosisAverageHours
      if (avgDiff < -2) return "mdi-arrow-down"
      if (avgDiff > 2) return "mdi-arrow-up"
      return "mdi-approximately-equal"
    },
  },
  created() {
    this.checkAndLoadData()
  },
  methods: {
    async fetchDataAndCalculateTarget() {
      if (!this.studioId || !this.planningStartDate || !this.planningEndDate || !this.weeklyTemplateData) {
        console.warn("fetchDataAndCalculateTarget skipped: Missing props.")
        this.loading = false
        this.loadingTarget = false
        return
      }
      this.loading = true
      this.loadingTarget = true
      console.log("Schritt 2: Lade Mitarbeiterdaten und berechne Bedarf...")
      try {
        const [allEmployees, allHolidays] = await Promise.all([
          api.shiftManagement.fetchShiftManagementEmployee(),
          api.shiftManagement.fetchAllHolidayEntries(),
        ])

        this.employees = allEmployees
          .filter((emp) => {
            const studioInfo = emp.studio?.find((s) => s._id === this.studioId)
            // ** KORREKTUR: Verwende getPlanningPercentage, um 0% MA *nach* temporärer Anpassung zu filtern **
            // (Wir behalten 0% MA vorerst hier, filtern aber später, oder verwenden die Basisprozente für den Filter)
            const basePercentage = emp.timeTrackerPercentage ?? studioInfo?.percentage ?? 0
            return studioInfo && basePercentage > 0 // Filtert MA mit 0% im Studio
          })
          .map((emp) => {
            const schoolDaysOfWeek = emp.schoolDays ? emp.schoolDays.map((d) => d.dayOfWeek) : []
            const storageKey = `planningSettings_${emp._id}_${this.studioId}`
            const savedSettings = JSON.parse(localStorage.getItem(storageKey)) || {}
            console.log(allHolidays, emp.user)

            const vacations = allHolidays
              .filter((h) => h.employee === emp.user)
              .map((h) => ({
                ...h,
                formattedRange: `${this.$moment(h.dates[0].date).format("DD.MM")} - ${this.$moment(h.dates[h.dates.length - 1].date).format(
                  "DD.MM"
                )}`,
              }))
              .filter((h) => {
                console.log(h.dates)
                return (
                  this.$moment(h.dates[0].date).isSameOrBefore(this.planningEndDate) &&
                  this.$moment(h.dates[h.dates.length - 1].date).isSameOrAfter(this.planningStartDate)
                )
              })

            return {
              ...emp,
              schoolDaysOfWeek,
              vacations,
              planningSettings: {
                percentages: savedSettings.percentages || {},
                recurringAbsence: savedSettings.recurringAbsence || [],
                isReadyForPlanning: savedSettings.isReadyForPlanning === true,
              },
            }
          })

        this.calculateTargetHours()
      } catch (error) {
        this.$toast.error("Mitarbeiterdaten/Bedarf konnten nicht geladen werden.")
        console.error("Fetch Data/Target Error:", error)
        this.employees = []
        this.targetHours = 0
        this.targetPercentage = 0
      } finally {
        this.loading = false
        if (this.loadingTarget) {
          this.loadingTarget = false
        }
      }
    },
    calculateTargetHours() {
      const templateToUse = this.weeklyTemplateData
      const shiftsToUse = this.availableShifts

      if (
        !this.planningStartDate ||
        !this.planningEndDate ||
        !Array.isArray(templateToUse) ||
        !Array.isArray(shiftsToUse) ||
        shiftsToUse.length === 0
      ) {
        console.warn("calculateTargetHours skipped: missing data", {
          start: this.planningStartDate,
          end: this.planningEndDate,
          template: templateToUse?.length,
          shifts: shiftsToUse?.length,
        })
        this.targetHours = 0
        this.targetPercentage = 0
        this.loadingTarget = false
        return
      }
      this.loadingTarget = true

      let totalHours = 0
      let workDaysInPeriod = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)
      let current = start.clone()
      const availableShiftsMap = new Map(shiftsToUse.map((s) => [s._id, s]))

      // TODO: Feiertage & Schulferien hier berücksichtigen

      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const dayTemplate = templateToUse.find((d) => d.dayOfWeek === dayOfWeekISO)

        // TODO: Prüfen ob Feiertag/Schulferien

        if (dayOfWeekISO < 6 /* && !isHoliday && !isSchoolHoliday */) {
          workDaysInPeriod++
        }

        if (dayTemplate && Array.isArray(dayTemplate.requiredShifts)) {
          dayTemplate.requiredShifts.forEach((reqShift) => {
            const shiftTemplateId = reqShift.shiftTemplateId?._id || reqShift.shiftTemplateId
            const shiftDetails = availableShiftsMap.get(shiftTemplateId)

            if (shiftDetails) {
              totalHours += this.calculateSingleShiftHours(shiftDetails)
            } else {
              console.warn(`Shift details not found for ID ${shiftTemplateId} in template on ${current.format("YYYY-MM-DD")}.`)
            }
          })
        }
        current.add(1, "day")
      }

      this.targetHours = totalHours

      // ** KORREKTUR: %-Berechnung basierend auf 8.4h pro Werktag **
      // Zuerst die durchschnittlichen benötigten Stunden pro Werktag
      const avgHoursPerWorkDay = workDaysInPeriod > 0 ? totalHours / workDaysInPeriod : 0
      // Dann diese Stunden ins Verhältnis zu 8.4h (100%) setzen
      this.targetPercentage = avgHoursPerWorkDay > 0 ? (avgHoursPerWorkDay / 8.4) * 100 : 0

      this.loadingTarget = false
    },
    calculateSingleShiftHours(shiftTemplate) {
      if (!shiftTemplate || !shiftTemplate.times) return 0
      return shiftTemplate.times.reduce((sum, time) => {
        if (time.type === "Arbeitszeit" && time.start && time.end) {
          const start = this.$moment(time.start, "HH:mm")
          const end = this.$moment(time.end, "HH:mm")
          if (end.isBefore(start)) end.add(1, "day")
          return sum + end.diff(start, "hours", true)
        }
        return sum
      }, 0)
    },
    getStudioInfo(employee) {
      if (!employee || !employee.studio) return null
      return employee.studio.find((s) => s._id === this.studioId)
    },
    getStudioName(studioId) {
      if (!this.storeStudios || this.storeStudios.length === 0) {
        const employeeWithStudio = this.employees.find((e) => e.studio.some((s) => s._id === studioId))
        if (employeeWithStudio) {
          const studioInfo = employeeWithStudio.studio.find((s) => s._id === studioId)
          return studioInfo.title || "Lade..."
        }
        return "Unbekannt"
      }
      const studio = this.storeStudios.find((s) => s._id === studioId)
      return studio ? studio.name : "Unbekannt"
    },
    getPlanningPercentage(employee, studioInfo) {
      if (!studioInfo && !employee.timeTrackerPercentage) return 0

      // Priorität 1: Temporäre Einstellung im LocalStorage
      const planningPercentages = employee.planningSettings?.percentages
      if (planningPercentages && planningPercentages[studioInfo._id] != null) {
        return planningPercentages[studioInfo._id]
      }
      // Priorität 2: Globales timeTrackerPercentage (falls gesetzt)
      if (employee.timeTrackerPercentage != null) {
        return employee.timeTrackerPercentage
      }
      // Priorität 3: Studio-spezifisches Pensum
      if (studioInfo) {
        return studioInfo.percentage
      }
      return 0 // Fallback
    },
    updatePercentage(employee, studioId, value) {
      if (!employee.planningSettings) Vue.set(employee, "planningSettings", {})
      if (!employee.planningSettings.percentages) Vue.set(employee.planningSettings, "percentages", {})
      Vue.set(employee.planningSettings.percentages, studioId, parseFloat(value) || 0)
      this.updatePlanningSettings(employee)
    },
    updateSchoolDays(employee) {
      const daysOfWeek = Array.isArray(employee.schoolDaysOfWeek) ? employee.schoolDaysOfWeek : []
      employee.schoolDays = daysOfWeek.map((dayValue) => ({
        dayOfWeek: dayValue,
        hours: 8.4,
      }))
      this.savePermanentSettings(employee)
    },
    updatePlanningSettings(employee) {
      const settingsToSave = {
        percentages: employee.planningSettings?.percentages || {},
        recurringAbsence: employee.planningSettings?.recurringAbsence || [],
        isReadyForPlanning: employee.planningSettings?.isReadyForPlanning || false,
      }
      const storageKey = `planningSettings_${employee._id}_${this.studioId}`
      try {
        localStorage.setItem(storageKey, JSON.stringify(settingsToSave))
        console.log(`Settings saved for ${employee.fullName} under key ${storageKey}:`, settingsToSave)
      } catch (e) {
        console.error("Error saving to localStorage:", e)
        this.$toast.error("Fehler beim lokalen Speichern der Einstellungen.")
      }
    },
    async savePermanentSettings(employee) {
      try {
        const settingsPayload = {
          isApprentice: employee.isApprentice,
          schoolDays: employee.schoolDays,
          cannotOpenOrCloseShift: employee.cannotOpenOrCloseShift,
        }
        await api.shiftManagement.updateEmployeePlanningSettings(employee._id, settingsPayload)
        this.$toast.success(`${employee.fullName} gespeichert!`)
      } catch (error) {
        this.$toast.error(`Fehler beim Speichern von ${employee.fullName}.`)
        console.error("Save Permanent Settings Error:", error)
        await this.fetchDataAndCalculateTarget()
      }
    },
    getConfirmationStatus() {
      const allEmployees = this.employees
      const readyEmployees = allEmployees.filter((emp) => emp.planningSettings?.isReadyForPlanning)
      const notReadyEmployees = allEmployees.filter((emp) => !emp.planningSettings?.isReadyForPlanning)
      return {
        allMarked: notReadyEmployees.length === 0,
        readyEmployeeIds: readyEmployees.map((emp) => emp.user),
        notReadyNames: notReadyEmployees.map((emp) => emp.fullName),
      }
    },
    // In shiftManagementMainDialogCreateEmployee.vue -> methods:
    calculateSollStunden(employeeData, planningSettings) {
      if (!this.planningStartDate || !this.planningEndDate || !employeeData) return 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)
      let businessDays = 0
      let current = start.clone()

      const studioInfo = employeeData.studio.find((s) => s._id === this.studioId)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo)

      // TODO: Feiertage aus Studio-Daten laden und hier verwenden

      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6

        // *** NEU: Check für Schulferien ***
        const isSchoolHoliday = this.schoolHolidays.some(
          (sh) => current.isBetween(sh.startDate, sh.endDate, null, "[]") // Prüft, ob 'current' zwischen Start und Ende liegt (inklusive)
        )

        let isSchoolDay = false
        // Ein Schultag zählt nur dann als "nicht Arbeitstag", WENN KEINE Schulferien sind.
        if (employeeData.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (employeeData.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }

        // TODO: Check isHoliday (normale Feiertage)

        // Ein Tag ist ein Arbeitstag, wenn es KEIN Wochenende, KEIN Schultag (ausserhalb der Ferien),
        // KEINE wiederkehrende Abwesenheit und für Lehrlinge KEIN Schulferientag ist.
        let isWorkDay = !isWeekend && !isSchoolDay

        // *** NEU: Zusätzliche Prüfung für Lehrlinge während Schulferien ***
        if (employeeData.isApprentice && isSchoolHoliday) {
          isWorkDay = false // Lehrlinge arbeiten nicht während Schulferien
        }

        if (isWorkDay) {
          businessDays++
        }
        current.add(1, "day")
      }

      return businessDays * (8.4 * (percentage / 100))
    },
    checkAndLoadData() {
      if (
        this.studioId &&
        this.planningStartDate &&
        this.planningEndDate &&
        Array.isArray(this.weeklyTemplateData) &&
        Array.isArray(this.availableShifts) &&
        this.availableShifts.length > 0
      ) {
        this.fetchDataAndCalculateTarget()
      } else {
        console.warn("checkAndLoadData: Nicht alle Props sind bereit.", {
          studio: !!this.studioId,
          start: !!this.planningStartDate,
          end: !!this.planningEndDate,
          template: Array.isArray(this.weeklyTemplateData),
          shifts: Array.isArray(this.availableShifts) && this.availableShifts.length > 0,
        })
        this.loading = true
        this.loadingTarget = true
      }
    },
  },
  watch: {
    // Lade Daten neu, wenn sich die Props ändern
    studioId: "checkAndLoadData",
    planningStartDate: "checkAndLoadData",
    planningEndDate: "checkAndLoadData",
    weeklyTemplateData: {
      handler(newData, oldData) {
        if (JSON.stringify(newData) !== JSON.stringify(oldData)) {
          console.log("Watcher: weeklyTemplateData changed, recalculating target...")
          this.calculateTargetHours()
        }
      },
      deep: true,
    },
    availableShifts: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0 && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          console.log("Watcher: availableShifts changed, triggering checkAndLoadData...")
          this.checkAndLoadData()
        }
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.employee-card {
  transition: box-shadow 0.3s ease-in-out, border-color 0.4s ease-in-out, background-color 0.4s ease-in-out;
  border: 1px solid transparent;
}
.employee-card:hover {
  box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.2);
}
.ready-card {
  border-color: var(--v-success-base);
  background-color: rgba(var(--v-success-base-rgb), 0.05);
}
.percentage-input.primary--text >>> .v-input__control .v-input__slot input {
  color: var(--v-primary-base) !important;
  font-weight: bold;
}
.percentage-input >>> .v-input__control .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding-right: 2px !important;
}
.percentage-input >>> .v-input__control .v-input__slot input[type="number"] {
  -moz-appearance: textfield;
}
.percentage-input >>> .v-input__control .v-input__slot input::-webkit-outer-spin-button,
.percentage-input >>> .v-input__control .v-input__slot input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
.ready-switch >>> .v-input--selection-controls__input {
  transform: scale(1.1);
}

/* Animation für den Fortschrittsbalken */
.progress-bar-animation >>> .v-progress-linear__determinate {
  transition: width 0.8s cubic-bezier(0.25, 0.8, 0.25, 1);
}
</style>
